'use client';

import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Globe, ExternalLink, AlertTriangle, TriangleAlert, Copy, Check, Crown, Coins } from 'lucide-react';
import { cn, truncateAddress, formatUSD, formatUSDCompact, parseGridId } from '@/lib/utils';
import { AvatarWithFlair, LevelBadge } from '@/components/prelaunch/avatar-with-flair';
import { isSafeExternalUrl } from '@/components/icons/social-icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CoinImage } from '@/components/shared';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useProfileModalOptional } from '@/contexts/profile-modal-context';
import type { V2Grid, V2TopHolder } from '@/lib/types/v2';
import { PLATFORM_ICON_URLS } from '@/lib/constants/protocol-branding';
import type { AssetCard } from './asset-deck';

export interface SocialLink {
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

interface GridIdentityCardProps {
  grid: V2Grid;
  creatorIdentity?: {
    address: string;
    displayName?: string;
    avatarUrl?: string;
    level?: number;
  };
  assetCards?: AssetCard[];
  assetAddress?: string;
  topHolders?: V2TopHolder[];
  totalSeats?: number;
  socialLinks?: SocialLink[];
  tvl?: number;
  rangeLabel?: string | null;
  taxRateBps?: number;
}

export function GridIdentityCard({
  grid,
  creatorIdentity,
  assetCards = [],
  assetAddress,
  totalSeats = 100,
  socialLinks = [],
  tvl,
  rangeLabel,
  taxRateBps,
}: GridIdentityCardProps) {
  const gridName = grid.name || `Grid #${parseGridId(grid.id).numericId}`;
  const profileModal = useProfileModalOptional();
  const [copied, setCopied] = useState(false);

  const [socialsOpen, setSocialsOpen] = useState(false);
  const socialsRef = useRef<HTMLDivElement>(null);
  const [tradeOpen, setTradeOpen] = useState(false);
  const tradeRef = useRef<HTMLDivElement>(null);
  const [pendingSocialUrl, setPendingSocialUrl] = useState<{ url: string; label: string } | null>(null);

  const copyAddress = () => {
    navigator.clipboard.writeText(assetAddress || grid.managerAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (!socialsOpen && !tradeOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (socialsOpen && socialsRef.current && !socialsRef.current.contains(e.target as Node)) setSocialsOpen(false);
      if (tradeOpen && tradeRef.current && !tradeRef.current.contains(e.target as Node)) setTradeOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [socialsOpen, tradeOpen]);

  const sortedAssets = [...assetCards].sort((a, b) => {
    const aTvl = a.tvl ? Number(a.tvl) : 0;
    const bTvl = b.tvl ? Number(b.tvl) : 0;
    return bTvl - aTvl;
  });

  const primaryAssetCard = sortedAssets[0];
  const primaryAdapterPlatform = primaryAssetCard?.platform ?? '';
  const primaryPairLabel = primaryAssetCard?.pairLabel ?? undefined;
  const isFlaunch = primaryAdapterPlatform === 'flaunch';
  const chainSlug = process.env.NEXT_PUBLIC_CHAIN_ID === '8453' ? 'base' : 'base-sepolia';
  const gridTvl = tvl ?? sortedAssets.reduce((sum, a) => {
    const assetTvl = a.tvl ? Number(a.tvl) / 1e6 : 0;
    return sum + assetTvl;
  }, 0);

  return (
    <div className="space-y-3">
      {/* Grid identity — inline image + name */}
      <div>
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <div className="w-10 h-10 rounded-lg overflow-hidden border border-border/50 bg-muted/30">
              <CoinImage src={grid.imageUrl} alt={gridName} symbol={gridName} />
            </div>
            {primaryAdapterPlatform && PLATFORM_ICON_URLS[primaryAdapterPlatform as keyof typeof PLATFORM_ICON_URLS] && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <img
                    src={PLATFORM_ICON_URLS[primaryAdapterPlatform as keyof typeof PLATFORM_ICON_URLS]}
                    alt={primaryAdapterPlatform}
                    className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border border-background bg-background"
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-[10px] px-1.5 py-0.5">
                  {primaryAdapterPlatform === 'uniswap-v4' ? 'Uniswap V4' :
                   primaryAdapterPlatform === 'uniswap-v3' ? 'Uniswap V3' :
                   primaryAdapterPlatform === 'flaunch' ? 'Flaunch' : 'Aerodrome'}
                  {primaryPairLabel && <span className="text-muted-foreground ml-1">({primaryPairLabel})</span>}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold truncate">{gridName}</h1>
              <button
                onClick={copyAddress}
                className="p-1 rounded hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground shrink-0"
                title="Copy contract address"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-win-green" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
            {primaryPairLabel && (
              <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
                <span className="font-medium text-foreground/80">{primaryPairLabel}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {(rangeLabel || gridTvl > 0 || (taxRateBps != null && taxRateBps > 0)) && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {rangeLabel && <span>{rangeLabel} LP</span>}
          {rangeLabel && gridTvl > 0 && <span>·</span>}
          {gridTvl > 0 && (
            <span className="font-mono text-foreground/70 inline-flex items-center gap-1">
              {formatUSDCompact(gridTvl)} TVL
              {gridTvl < 1000 && (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <TriangleAlert className="h-3 w-3 text-amber-500 shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-[10px] px-1.5 py-0.5">
                    Low TVL
                  </TooltipContent>
                </Tooltip>
              )}
            </span>
          )}
          {taxRateBps != null && taxRateBps > 0 && (
            <>
              {(rangeLabel || gridTvl > 0) && <span>·</span>}
              <span className="font-mono text-foreground/70">{taxRateBps / 100}%/wk tax</span>
            </>
          )}
        </div>
      )}

      {/* Socials + Trade */}
      {(socialLinks.length > 0 || assetAddress) && (
        <div className="flex items-center gap-2">
          {socialLinks.length > 0 && (
            <div ref={socialsRef} className="relative">
              <button
                onClick={() => { setSocialsOpen(!socialsOpen); setTradeOpen(false); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border rounded-md hover:bg-muted/50 transition-colors"
              >
                <Globe className="h-3 w-3" />
                Socials
                <ChevronDown className={cn('h-3 w-3 transition-transform', socialsOpen && 'rotate-180')} />
              </button>
              {socialsOpen && (
                <div className="absolute z-50 w-48 left-0 mt-1 rounded-lg border bg-card shadow-lg animate-fade-in overflow-hidden">
                  {socialLinks.map(({ url, icon: Icon, label }) => (
                    <button
                      key={label}
                      onClick={() => { setSocialsOpen(false); setPendingSocialUrl({ url, label }); }}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors group w-full text-left"
                    >
                      <Icon className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="flex-1 text-sm font-medium">{label}</span>
                      <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {assetAddress && (
            <div ref={tradeRef} className="relative">
              <button
                onClick={() => { setTradeOpen(!tradeOpen); setSocialsOpen(false); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border rounded-md hover:bg-muted/50 transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                Trade
                <ChevronDown className={cn('h-3 w-3 transition-transform', tradeOpen && 'rotate-180')} />
              </button>
              {tradeOpen && (
                <div className="absolute z-50 w-48 left-0 mt-1 rounded-lg border bg-card shadow-lg animate-fade-in overflow-hidden">
                  {isFlaunch && (
                    <a
                      href={`https://flaunch.gg/${chainSlug}/coin/${assetAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setTradeOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors group"
                    >
                      <img src="/flaunch-icon.png" alt="Flaunch" className="w-4 h-4" />
                      <span className="flex-1 text-sm font-medium">Flaunch</span>
                      <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                    </a>
                  )}
                  <a
                    href={`https://app.uniswap.org/swap?chain=base&inputCurrency=ETH&outputCurrency=${assetAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setTradeOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors group"
                  >
                    <img src="/icons/uniswap.ico" alt="Uniswap" className="w-4 h-4" />
                    <span className="flex-1 text-sm font-medium">Uniswap</span>
                    <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </a>
                  <a
                    href={`https://dexscreener.com/base/${assetAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setTradeOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors group"
                  >
                    <img src="/icons/dexscreener.ico" alt="Dexscreener" className="w-4 h-4" />
                    <span className="flex-1 text-sm font-medium">Dexscreener</span>
                    <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Created by */}
      {creatorIdentity && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>Created by</span>
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              {profileModal ? (
                <button
                  type="button"
                  onClick={() => profileModal.openProfile(creatorIdentity.address)}
                  className="flex items-center gap-1.5 hover:text-foreground transition-colors"
                >
                  <AvatarWithFlair
                    avatarUrl={creatorIdentity.avatarUrl}
                    address={creatorIdentity.address}
                    level={creatorIdentity.level ?? 0}
                    size="xs"
                  />
                  <span className="font-medium text-foreground/80 truncate max-w-[120px]">
                    {creatorIdentity.displayName || truncateAddress(creatorIdentity.address)}
                  </span>
                </button>
              ) : (
                <span className="flex items-center gap-1.5">
                  <AvatarWithFlair
                    avatarUrl={creatorIdentity.avatarUrl}
                    address={creatorIdentity.address}
                    level={creatorIdentity.level ?? 0}
                    size="xs"
                  />
                  <span className="font-medium text-foreground/80 truncate max-w-[120px]">
                    {creatorIdentity.displayName || truncateAddress(creatorIdentity.address)}
                  </span>
                </span>
              )}
            </TooltipTrigger>
            <TooltipContent side="bottom" className="p-2">
              <div className="flex items-center gap-2">
                <AvatarWithFlair
                  avatarUrl={creatorIdentity.avatarUrl}
                  address={creatorIdentity.address}
                  level={creatorIdentity.level ?? 0}
                  size="sm"
                />
                <div>
                  <p className="font-medium text-sm">
                    {creatorIdentity.displayName || truncateAddress(creatorIdentity.address)}
                  </p>
                  <p className="text-[10px] text-muted-foreground font-mono">
                    {truncateAddress(creatorIdentity.address)}
                  </p>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* External link interstitial warning */}
      <Dialog open={!!pendingSocialUrl} onOpenChange={(open) => !open && setPendingSocialUrl(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              External Link Warning
            </DialogTitle>
            <DialogDescription className="text-left pt-2 space-y-2">
              <p>You are about to visit an external link submitted by the grid creator:</p>
              <p className="font-mono text-xs bg-muted/50 rounded-md px-3 py-2 break-all text-foreground">
                {pendingSocialUrl?.url}
              </p>
              <p>
                Takeover is a permissionless protocol. These links are <span className="font-semibold text-foreground">not verified</span> and could be misleading.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setPendingSocialUrl(null)}>
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                if (pendingSocialUrl && isSafeExternalUrl(pendingSocialUrl.url)) {
                  window.open(pendingSocialUrl.url, '_blank', 'noopener,noreferrer');
                }
                setPendingSocialUrl(null);
              }}
            >
              Continue to {pendingSocialUrl?.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HolderTooltip({ holder, totalSeats, isLongestHolder }: { holder: V2TopHolder; totalSeats: number; isLongestHolder?: boolean }) {
  const profileModal = useProfileModalOptional();
  const level = holder.level ?? 0;
  const heldForStr = (() => {
    if (!holder.heldSince) return undefined;
    const seconds = Math.floor(Date.now() / 1000) - holder.heldSince;
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''}`;
  })();
  const displayLabel = holder.displayName || truncateAddress(holder.address);
  return (
    <div className="flex items-start gap-3 py-1 min-w-[180px]">
      <div className="flex flex-col items-center shrink-0">
        <AvatarWithFlair
          avatarUrl={holder.avatarUrl}
          address={holder.address}
          level={level}
          size="sm"
        />
      </div>
      <div className="flex-1 min-w-0">
        {profileModal ? (
          <button
            type="button"
            className="font-medium truncate block text-left hover:underline hover:text-primary"
            onClick={(e) => {
              e.stopPropagation();
              profileModal.openProfile(holder.address);
            }}
          >
            {displayLabel}
          </button>
        ) : (
          <span className="font-medium truncate block">{displayLabel}</span>
        )}
        {level > 0 && <LevelBadge level={level} className="text-[10px]" />}
        <div className="text-xs text-muted-foreground space-y-0.5 mt-1">
          <div className="flex justify-between gap-4">
            <span>Tiles:</span>
            <span className="font-mono font-medium text-foreground">{holder.seatCount}</span>
          </div>
          {heldForStr && (
            <div className="flex justify-between gap-4">
              <span>Held for:</span>
              <span className="flex items-center gap-1 font-mono font-medium text-foreground">
                {isLongestHolder && <Crown className="h-3 w-3 text-amber-500" />}
                {heldForStr}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Standalone Holders Card
// =============================================================================

interface HoldersCardProps {
  topHolders: V2TopHolder[];
  totalSeats?: number;
  holderCount?: number;
}

function HolderRow({ holder, idx, totalSeats, isLongest }: { holder: V2TopHolder; idx: number; totalSeats: number; isLongest: boolean }) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const profileModal = useProfileModalOptional();
  const pct = ((holder.seatCount / totalSeats) * 100).toFixed(0);

  const handleClick = (e: React.MouseEvent) => {
    if ('ontouchstart' in window && !tooltipOpen) {
      e.preventDefault();
      setTooltipOpen(true);
      return;
    }
    if (profileModal) {
      profileModal.openProfile(holder.address);
    }
  };

  const rowClass = "flex items-center gap-2 py-1.5 hover:bg-muted/30 rounded-md px-1.5 transition-colors w-full text-left";
  const rowInner = (
    <>
      {isLongest ? (
        <Crown className="w-4 h-3.5 text-amber-500 shrink-0" />
      ) : (
        <span className="w-4 text-[10px] font-mono text-center shrink-0 text-muted-foreground">
          {idx + 1}
        </span>
      )}
      <AvatarWithFlair
        avatarUrl={holder.avatarUrl}
        address={holder.address}
        level={holder.level ?? 0}
        size="xs"
      />
      <span className="text-xs font-medium truncate flex-1">
        {holder.displayName || truncateAddress(holder.address, 3)}
      </span>
      <span className="text-[10px] font-mono text-muted-foreground shrink-0">
        {holder.seatCount} ({pct}%)
      </span>
    </>
  );

  return (
    <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen} delayDuration={150}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          className={rowClass}
        >
          {rowInner}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="center" className="p-2">
        <HolderTooltip holder={holder} totalSeats={totalSeats} isLongestHolder={isLongest} />
      </TooltipContent>
    </Tooltip>
  );
}

export function HoldersCard({ topHolders, totalSeats = 100 }: HoldersCardProps) {
  const visible = topHolders.slice(0, 10);

  if (visible.length === 0) return null;

  const longestHolderAddr = visible.reduce<string | null>((best, h) => {
    if (!h.heldSince) return best;
    const bestHolder = visible.find(v => v.address === best);
    if (!bestHolder?.heldSince) return h.address;
    return h.heldSince < bestHolder.heldSince ? h.address : best;
  }, null);

  return (
    <Card>
      <div className="px-3 py-2 border-b border-border/50">
        <span className="text-sm font-semibold">Top Tile Holders</span>
      </div>
      <CardContent className="p-3 space-y-1">
        {visible.map((holder, idx) => (
          <HolderRow
            key={holder.address}
            holder={holder}
            idx={idx}
            totalSeats={totalSeats}
            isLongest={holder.address === longestHolderAddr}
          />
        ))}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Creator Stats Card
// =============================================================================

interface CreatorStatsCardProps {
  grid: V2Grid;
  creatorSeatPrice: number;
}

export function CreatorStatsCard({ grid, creatorSeatPrice }: CreatorStatsCardProps) {
  const minted = grid.seatsSoldFromCreator;
  const total = grid.totalSeats;
  const pct = total > 0 ? Math.round((minted / total) * 100) : 0;

  const isFreeMint = creatorSeatPrice === 0;
  const seatSaleRevenue = creatorSeatPrice * minted;
  const taxRevenue = Number(grid.totalTaxCollected) / 1e6;
  const totalProceeds = seatSaleRevenue + taxRevenue;

  return (
    <Card>
      <div className="px-3 py-2 border-b border-border/50">
        <span className="text-sm font-semibold flex items-center gap-2">
          <Coins className="h-4 w-4 text-amber-500" />
          Creator Stats
        </span>
      </div>
      <CardContent className="p-3 space-y-3">
        {/* Tiles Minted */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Tiles Minted</span>
            <span className="text-xs font-bold font-mono text-amber-400">{minted}/{total}</span>
          </div>
          <div className="w-full h-1.5 bg-muted/40 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Proceeds */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Total Proceeds</span>
          {isFreeMint ? (
            <span className="text-xs font-bold text-amber-400">Free Mint</span>
          ) : (
            <span className="text-xs font-bold font-mono text-amber-400">{formatUSD(totalProceeds)}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

